import { useTranslation } from 'react-i18next'
import { H1, H2, LoadingDots } from '@/components/atoms'
import {
  Toast,
  LinkCard,
  Sidebar,
  PolicyCard,
  AppDownloadBanner,
} from '@/components/molecules'
import { usePaymentPage } from './page.payment.hook'
import type { ActiveModal } from './page.payment.hook'
import { PaymentInUpdatePage } from './in/page.payment-in-update'
import { PaymentOutUpdatePage } from './out/page.payment-out-update'
import { ClaimDocumentIcon, PolicyDocumentIcon } from '@/components/atoms/icons'

export function PaymentLayout() {
  return <PaymentPage />
}

export function PaymentPage({
  initialModal,
}: { initialModal?: ActiveModal } = {}) {
  const { t } = useTranslation()
  const {
    user,
    loading,
    policiesLoading,
    policies,
    showSuccessToast,
    dismissToast,
    activeModal,
    openPayInModal,
    openPayOutModal,
    closeModal,
    handlePayOutSaveSuccess,
  } = usePaymentPage(initialModal)

  if (loading) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center">
        <LoadingDots />
      </div>
    )
  }

  const firstName = user?.givenName ?? user?.name ?? ''

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-5xl flex-col">
      <div className="p-6 pt-8">
        {firstName && (
          <H1
            data-testid="payment-greeting"
            className="mb-8 font-['Merriweather'] text-[2.625rem]! leading-12! font-semibold text-neutral-900"
          >
            {t('payment.greeting', { name: firstName })}
          </H1>
        )}

        {showSuccessToast && (
          <Toast
            variant="success"
            message={t('payment.updatePayOut.success')}
            onDismiss={dismissToast}
          />
        )}

        <div className="space-y-6">
          <AppDownloadBanner />

          <div>
            <H2
              data-testid="preferences-heading"
              className="mb-6 text-base! leading-5! font-bold"
            >
              {t('payment.preferences')}
            </H2>
            <div className="flex flex-col gap-x-6 gap-y-2 md:flex-row">
              <LinkCard
                label={t('payment.payIn.title')}
                onClick={openPayInModal}
                data-testid="pay-in-card"
                icon={
                  <PolicyDocumentIcon className="text-indigo-base h-6 w-6" />
                }
              />
              <LinkCard
                label={t('payment.payOut.title')}
                onClick={openPayOutModal}
                data-testid="pay-out-card"
                icon={
                  <ClaimDocumentIcon className="text-indigo-base h-6 w-6" />
                }
              />
            </div>
          </div>

          <div>
            <H2
              data-testid="billing-heading"
              className="mb-6 text-base! leading-5! font-bold"
            >
              {t('payment.billing')}
            </H2>
            {policiesLoading ? (
              <div className="flex justify-center py-8">
                <LoadingDots />
              </div>
            ) : (
              <div className="flex gap-6 overflow-auto md:flex-row md:flex-wrap">
                {policies.map((policy, index) => (
                  <PolicyCard
                    key={policy.id ?? `${policy.policyType}-${index}`}
                    policy={policy}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <Sidebar
        isOpen={activeModal === 'payIn'}
        onClose={closeModal}
        keepMounted
        hideCloseButton
      >
        <PaymentInUpdatePage
          onClose={closeModal}
          isVisible={activeModal === 'payIn'}
        />
      </Sidebar>

      <Sidebar
        isOpen={activeModal === 'payOut'}
        onClose={closeModal}
        title={t('payment.payOut.title')}
      >
        <PaymentOutUpdatePage
          onClose={closeModal}
          onSuccess={handlePayOutSaveSuccess}
        />
      </Sidebar>
    </div>
  )
}
